import React, { useState } from "react";
import { Link } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { Helmet } from "react-helmet";
import Grid from "@mui/material/Grid2";
import { motion } from "framer-motion";
import { ThemeProvider } from "@mui/material/styles";
import seniorLivingTheme from "./seniorLivingTheme";
import AboutUs from "../About";
import Footer from "../Footer";
import AsSeenInSection from "../AsSeenIn";
import ThisOrThat from "../Games/ThisOrThat";
import Match3Game from "../FastFood/Match3Game";
import BaseMemoryGame from "../../games/Memory/BaseMemoryGame";
import SeniorLivingMemoryGame from "./SeniorLivingMemoryGame";

// Senior Living specific content
const GAME_PREVIEWS = [
  {
    key: "memory",
    title: "Memory Match",
    description: "Tap-to-reveal memory tiles with nostalgic imagery",
    emoji: "🧠",
    color: "#7B8B59",
  },
  {
    key: "this-or-that",
    title: "This or That: Classic Edition",
    description: "Choose between two options (e.g., Beatles or Elvis?)",
    emoji: "🎵",
    color: "#A5B788",
  },
  {
    key: "crossword",
    title: "Crossword Lite",
    description: "Easy-entry word puzzles with larger grids",
    emoji: "📝",
    color: "#FFD97D",
  },
  {
    key: "spin",
    title: "Spin to Smile",
    description: "Spin for fun challenges, trivia, or jokes",
    emoji: "🎡",
    color: "#9B3C3C",
  },
];

const SENIOR_LIVING_PAIRS = [
  { left: "🎸 Elvis", right: "🎵 The Beatles" },
  { left: "☕️ Coffee", right: "🫖 Tea" },
  { left: "🚶 Morning Walk", right: "🎲 Bingo Night" },
  { left: "🎤 Frank Sinatra", right: "🎵 Aretha Franklin" },
  { left: "📝 Crossword Puzzles", right: "🧩 Jigsaw Puzzles" },
];

const FAQS = [
  {
    question: "Do we need special hardware?",
    answer:
      "No special equipment required. Our games work on any tablet, smart TV, or resident's personal device. We'll help you set up with your existing technology.",
    icon: "📱",
    color: "#7B8B59",
  },
  {
    question: "Is this suitable for residents with memory loss?",
    answer:
      "Yes! Our games are designed with accessibility in mind. Large text, simple controls, and voice-assisted options make them enjoyable for all cognitive levels.",
    icon: "🧠",
    color: "#A5B788",
  },
  {
    question: "Can staff assist with setup?",
    answer:
      "Absolutely. We provide comprehensive training and ongoing support. Most communities have their activity directors running the games within minutes.",
    icon: "👥",
    color: "#FFD97D",
  },
  {
    question: "Can we customize to birthdays, holidays, or our event calendar?",
    answer:
      "Yes! We can theme games around your community's events, holidays, or even resident birthdays. It's all part of creating a personalized experience.",
    icon: "🎉",
    color: "#9B3C3C",
  },
  {
    question: "How much time does this take to set up each day?",
    answer:
      "None. We handle daily game updates automatically. Just turn on the tablet or TV and go.",
    icon: "⏰",
    color: "#7B8B59",
  },
];

const SeniorLivingHome = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <ThemeProvider theme={seniorLivingTheme}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          overflowX: "hidden",
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Helmet>
          <title>
            Digital Games for Senior Living Communities – Flying Comet Games
          </title>
          <meta
            name="description"
            content="Engage residents with accessible, delightful digital games designed for senior living communities. Bring families together through play."
          />
        </Helmet>

        {/* Hero Section */}
        <Box
          sx={{
            background: `linear-gradient(rgba(252, 239, 227, 0.97), rgba(252, 239, 227, 0.97)), url('https://images.unsplash.com/photo-1573497620053-ea5300f94f21?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            color: theme.palette.text.primary,
            pt: { xs: 8, md: 12 },
            pb: { xs: 12, md: 16 },
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "100px",
              background:
                "linear-gradient(to bottom, transparent, rgba(252, 239, 227, 0.9))",
            },
          }}
        >
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeIn}
            transition={{ duration: 0.6 }}
          >
            <Grid
              container
              spacing={8}
              alignItems="center"
              justifyContent="center"
              sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, md: 4 } }}
            >
              <Grid item size={{ xs: 12, md: 7 }}>
                <Typography
                  sx={{
                    fontSize: "4.5rem",
                    fontWeight: 800,
                    mb: 1,
                    lineHeight: 1.1,
                    color: theme.palette.primary.main,
                    textShadow: "0 2px 4px rgba(0,0,0,0.1)",
                  }}
                >
                  Digital games for senior living communities
                </Typography>
                <Typography
                  sx={{
                    fontSize: "1.75rem",
                    fontWeight: 400,
                    mb: 4,
                    opacity: 0.9,
                    color: theme.palette.text.primary,
                    maxWidth: "600px",
                  }}
                >
                  Engage residents, delight families, and free up staff — no
                  setup required.
                </Typography>
                <Button
                  variant="contained"
                  href="https://cal.com/calli-fuchigami/15min"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: "#FFF",
                    px: 6,
                    py: 3,
                    borderRadius: "100px",
                    fontSize: theme.typography.button.fontSize,
                    fontWeight: 700,
                    textTransform: "none",
                    boxShadow: "0 8px 16px rgba(123,139,89,0.2)",
                    minHeight: "2.5em",
                    mb: { xs: 6, md: 0 },
                    "&:hover": {
                      backgroundColor: theme.palette.primary.dark,
                      transform: "translateY(-2px)",
                      boxShadow: "0 12px 24px rgba(123,139,89,0.3)",
                    },
                  }}
                >
                  Play a sample game →
                </Button>
              </Grid>
              <Grid
                item
                size={{ xs: 12, md: 5 }}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    borderRadius: "24px",
                    overflow: "hidden",
                    boxShadow: "0 20px 40px rgba(0,0,0,0.15)",
                    border: "12px solid #2C2C2C",
                    position: "relative",
                    background: "#2C2C2C",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      transform: "translateY(-4px) rotate(-1deg)",
                      boxShadow: "0 24px 48px rgba(0,0,0,0.2)",
                    },
                    "&::before": {
                      content: '""',
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      height: "30px",
                      background:
                        "linear-gradient(to bottom, rgba(0,0,0,0.2), transparent)",
                      borderRadius: "12px 12px 0 0",
                      zIndex: 1,
                    },
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      border: "2px solid rgba(255,255,255,0.1)",
                      zIndex: 1,
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      borderRadius: "12px",
                      overflow: "hidden",
                      position: "relative",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background:
                          "linear-gradient(45deg, rgba(255,217,125,0.1), transparent)",
                        zIndex: 1,
                        pointerEvents: "none",
                      },
                    }}
                  >
                    <ThisOrThat
                      pairs={SENIOR_LIVING_PAIRS}
                      brandColors={{
                        primary: "#A5B788",
                        secondary: "#FFD97D",
                        background: "#FEF5F5",
                        text: "#222222",
                        success: "#7B8B59",
                        neutral: "#FFD97D",
                        progressBg: "#FEF5F5",
                        white: "#fff",
                        black: "#222222",
                      }}
                    />
                  </Box>
                </Box>
                <Typography
                  sx={{
                    mt: 3,
                    fontSize: theme.typography.body1.fontSize,
                    color: theme.palette.text.secondary,
                    textAlign: "center",
                    maxWidth: "400px",
                  }}
                >
                  Games designed for gentle play, big laughs, and shared
                  moments.
                </Typography>
              </Grid>
            </Grid>
          </motion.div>
        </Box>

        {/* Value Prop Section */}
        <Box
          py={12}
          sx={{
            position: "relative",

            "&::after": {
              content: '""',
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "1px",
            },
          }}
        >
          <Box maxWidth="1200px" mx="auto" px={4}>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ duration: 0.6 }}
            >
              <Typography
                sx={{
                  fontSize: theme.typography.h1.fontSize,
                  fontWeight: 800,
                  mb: 2,
                  textAlign: "center",
                  color: theme.palette.primary.main,
                  position: "relative",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: -16,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "80px",
                    height: "4px",
                    background: theme.palette.secondary.main,
                    borderRadius: "2px",
                  },
                }}
              >
                Easy for Everyone
              </Typography>
              <Typography
                sx={{
                  fontSize: theme.typography.body2.fontSize,
                  fontWeight: 400,
                  mb: 5,
                  textAlign: "center",
                  color: theme.palette.text.primary,
                  opacity: 0.85,
                }}
              >
                Large fonts, calming colors, and simple tap-to-play design work
                for all abilities.
              </Typography>
              <Grid
                container
                spacing={4}
                sx={{ mb: 4 }}
                justifyContent="center"
              >
                {[
                  {
                    title: "Easy for Everyone",
                    description:
                      "Large fonts, calming colors, and simple tap-to-play design work for all abilities.",
                    icon: "👓",
                    color: theme.palette.primary.main,
                  },
                  {
                    title: "Brings People Together",
                    description:
                      "Designed for residents, families, and staff to enjoy — from morning coffee to family visits.",
                    icon: "👨‍👩‍👧‍👦",
                    color: theme.palette.primary.light,
                  },
                  {
                    title: "New Games Every Day",
                    description:
                      "We deliver a fresh, themed game daily. No extra work for your team.",
                    icon: "✨",
                    color: theme.palette.secondary.main,
                  },
                ].map((feature, idx) => (
                  <Grid item size={{ xs: 12, md: 4 }} key={idx}>
                    <Box
                      sx={{
                        backgroundColor: "#FFF",
                        borderRadius: "10px",
                        p: 5,
                        minHeight: 260,
                        width: { xs: "100%", md: 340 },
                        transition: "all 0.3s ease-in-out",
                        border: "1px solid rgba(0,0,0,0.1)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                        cursor: "pointer",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        position: "relative",
                        "&:hover": {
                          transform: "translateY(-8px)",
                          boxShadow: `0 20px 40px ${feature.color}22`,
                          borderColor: feature.color,
                          backgroundColor: "rgba(255,255,255,0.97)",
                        },
                      }}
                    >
                      <Box mb={3} minHeight={120}>
                        <Typography
                          variant="h4"
                          sx={{
                            fontSize: theme.typography.h4.fontSize,
                            fontWeight: 700,
                            mb: 1,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            color: "#222222",
                          }}
                        >
                          <span
                            style={{ fontSize: theme.typography.h3.fontSize }}
                          >
                            {feature.icon}
                          </span>{" "}
                          {feature.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: theme.typography.body1.fontSize,
                            lineHeight: 1.7,
                            color: theme.palette.text.primary,
                          }}
                        >
                          {feature.description}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          height: 8,
                          width: "100%",
                          background: feature.color,
                          borderRadius: "0 0 10px 10px",
                          position: "absolute",
                          left: 0,
                          bottom: 0,
                        }}
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </motion.div>
          </Box>
        </Box>

        {/* How It Works Section */}
        <Box
          py={12}
          sx={{
            background: `linear-gradient(rgba(252, 239, 227, 0.97), rgba(252, 239, 227, 0.97)), url('https://www.transparenttextures.com/patterns/linen.png')`,
            position: "relative",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "1px",
              background:
                "linear-gradient(to right, transparent, rgba(165,183,136,0.2), transparent)",
            },
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "url('https://www.transparenttextures.com/patterns/restaurant-pattern.png')",
              opacity: 0.05,
              pointerEvents: "none",
            },
          }}
        >
          <Box maxWidth="1200px" mx="auto" px={4}>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ duration: 0.6 }}
            >
              <Typography
                sx={{
                  fontSize: theme.typography.h1.fontSize,
                  fontWeight: 800,
                  mb: 2,
                  textAlign: "center",
                  color: theme.palette.primary.main,
                  position: "relative",
                  "&::after": {
                    content: '""',
                    position: "absolute",
                    bottom: -16,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "80px",
                    height: "4px",
                    background: theme.palette.secondary.main,
                    borderRadius: "2px",
                  },
                }}
              >
                How It Works
              </Typography>
              <Typography
                sx={{
                  fontSize: theme.typography.body2.fontSize,
                  fontWeight: 400,
                  mb: 5,
                  textAlign: "center",
                  color: theme.palette.text.primary,
                  opacity: 0.85,
                }}
              >
                Takes less than 7 days to launch. No app. No training. Just tap
                and play.
              </Typography>
              <Grid
                container
                spacing={4}
                justifyContent="center"
                alignItems="stretch"
              >
                {[
                  {
                    step: "1",
                    icon: "🧩",
                    title: "Pick Your Games",
                    description:
                      "Choose from memory games, trivia, word games, and more.",
                    color: theme.palette.primary.main,
                  },
                  {
                    step: "2",
                    icon: "🪶",
                    title: "We Handle Setup",
                    description:
                      "Games are updated daily and can be customized to your calendar.",
                    color: theme.palette.secondary.main,
                  },
                  {
                    step: "3",
                    icon: "😁",
                    title: "Residents Play",
                    description:
                      "Works on tablets, smart TVs, or phones — just tap and play.",
                    color: theme.palette.primary.light,
                  },
                ].map((step, idx) => (
                  <Grid
                    item
                    size={{ xs: 12, md: 4 }}
                    key={idx}
                    sx={{ display: "flex" }}
                  >
                    <Box
                      sx={{
                        backgroundColor: "#FFF",
                        borderRadius: "16px",
                        p: 5,
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                        border: "1px solid rgba(0,0,0,0.1)",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
                        position: "relative",
                        transition: "all 0.3s ease-in-out",
                        minWidth: 0,
                        flex: 1,
                        justifyContent: "flex-start",
                        "&:hover": {
                          transform: "translateY(-8px)",
                          boxShadow: `0 20px 40px ${step.color}22`,
                          borderColor: step.color,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: "50%",
                          background: `${step.color}15` || "#F5F5F5",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mb: 2,
                          boxShadow: `0 4px 12px ${step.color}22`,
                        }}
                      >
                        <Typography
                          sx={{ fontSize: theme.typography.h3.fontSize }}
                        >
                          {step.icon}
                        </Typography>
                      </Box>
                      <Box
                        sx={{
                          position: "absolute",
                          top: -18,
                          left: "50%",
                          transform: "translateX(-50%)",
                          width: 36,
                          height: 36,
                          background: step.color,
                          color: "#FFF",
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: theme.typography.h4.fontSize,
                          fontWeight: 700,
                          boxShadow: `0 2px 8px ${step.color}33`,
                          zIndex: 2,
                        }}
                      >
                        {step.step}
                      </Box>
                      <Typography
                        sx={{
                          fontSize: theme.typography.h4.fontSize,
                          fontWeight: 700,
                          mb: 1,
                          mt: 2,
                          color: "#222222",
                        }}
                      >
                        {step.title}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: theme.typography.body1.fontSize,
                          lineHeight: 1.7,
                          color: theme.palette.text.primary,
                        }}
                      >
                        {step.description}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </motion.div>
          </Box>
        </Box>

        {/* Try It Yourself Section */}
        <Box
          sx={{
            py: { xs: 8, md: 12 },
            px: { xs: 2, md: 4 },
            backgroundColor: theme.palette.background.accent,
          }}
        >
          <Grid container spacing={6} sx={{ maxWidth: 1200, mx: "auto" }}>
            <Grid item size={{ xs: 12 }}>
              <Typography
                variant="h2"
                sx={{
                  textAlign: "center",
                  mb: 6,
                  color: theme.palette.primary.main,
                }}
              >
                Experience it in 60 seconds
              </Typography>
            </Grid>
            <Grid item size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  p: 4,
                  height: "100%",
                  backgroundColor: "#FFF",
                  borderRadius: "24px",
                  boxShadow: "0 8px 32px rgba(92, 75, 58, 0.1)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  minHeight: 400,
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: theme.typography.h3.fontSize,
                    mb: 3,
                    color: theme.palette.primary.main,
                  }}
                >
                  Try Memory Match
                </Typography>
                <Box
                  sx={{
                    width: "100%",
                    maxWidth: 500,
                    height: 500,
                    backgroundColor: "#F5F5F5",
                    borderRadius: "12px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 4,
                  }}
                >
                  <SeniorLivingMemoryGame />
                </Box>
                <Button
                  variant="contained"
                  href="https://cal.com/calli-fuchigami/15min"
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    backgroundColor: theme.palette.primary.main,
                    color: "#FFF",
                    px: 4,
                    py: 2,
                    borderRadius: "100px",
                    fontSize: theme.typography.button.fontSize,
                    fontWeight: 600,
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: theme.palette.primary.dark,
                      transform: "translateY(-2px)",
                    },
                  }}
                >
                  Book a 15-minute demo →
                </Button>
                <Typography
                  sx={{
                    mt: 2,
                    fontSize: theme.typography.body1.fontSize,
                    color: theme.palette.text.secondary,
                    textAlign: "center",
                    fontStyle: "italic",
                  }}
                >
                  We'll walk you through the games and create a branded sample
                  just for your community.
                </Typography>
              </Box>
            </Grid>
            <Grid item size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  p: 4,
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    mb: 3,
                    color: theme.palette.text.primary,
                  }}
                >
                  Experience the simplicity
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 4,
                    color: theme.palette.text.secondary,
                    fontSize: theme.typography.body1.fontSize,
                  }}
                >
                  Our games are designed to be instantly engaging, with large,
                  clear text and simple tap interactions. No complex controls or
                  confusing menus — just pure enjoyment.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  {[
                    "Large, easy-to-read text",
                    "Simple tap interactions",
                    "Gentle color schemes",
                    "Voice-assisted options",
                  ].map((feature, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Typography
                        sx={{
                          color: theme.palette.primary.main,
                          fontSize: theme.typography.h3.fontSize,
                        }}
                      >
                        ✓
                      </Typography>
                      <Typography
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: theme.typography.body1.fontSize,
                        }}
                      >
                        {feature}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* FAQ Section */}
        <Box
          sx={{
            py: { xs: 8, md: 12 },
            px: { xs: 2, md: 4 },
            backgroundColor: theme.palette.background.accent,
          }}
        >
          <Grid container spacing={4} sx={{ maxWidth: 1200, mx: "auto" }}>
            <Grid item size={{ xs: 12 }}>
              <Typography
                variant="h2"
                sx={{
                  textAlign: "center",
                  mb: 6,
                  color: theme.palette.primary.main,
                }}
              >
                Frequently Asked Questions
              </Typography>
            </Grid>
            {FAQS.map((faq, index) => (
              <Grid item size={{ xs: 12, md: 6 }} key={index}>
                <Box
                  sx={{
                    p: 4,
                    height: "100%",
                    backgroundColor: "#FFF",
                    borderRadius: "24px",
                    boxShadow: "0 8px 32px rgba(92, 75, 58, 0.1)",
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      mb: 2,
                      color: "#222222",
                    }}
                  >
                    {faq.icon} {faq.question}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: theme.typography.body1.fontSize,
                    }}
                  >
                    {faq.answer}
                  </Typography>
                </Box>
              </Grid>
            ))}
            <Grid item size={{ xs: 12 }}>
              <Box
                sx={{
                  p: 4,
                  backgroundColor: "#FFF",
                  borderRadius: "24px",
                  boxShadow: "0 8px 32px rgba(92, 75, 58, 0.1)",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    color: theme.palette.text.secondary,
                    fontSize: theme.typography.body1.fontSize,
                    fontStyle: "italic",
                  }}
                >
                  Flexible pricing available for communities of all sizes —
                  reach out for a custom quote.
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Final CTA Section */}
        <Box
          sx={{
            py: { xs: 8, md: 12 },
            px: { xs: 2, md: 4 },
            backgroundColor: theme.palette.primary.main,
            color: "#FFF",
          }}
        >
          <Grid
            container
            spacing={4}
            sx={{ maxWidth: 1200, mx: "auto", textAlign: "center" }}
          >
            <Grid item size={{ xs: 12 }}>
              <Typography
                variant="h2"
                sx={{
                  mb: 4,
                  color: "#FFF",
                }}
              >
                Want to see your logo on the game? Let's build you a demo.
              </Typography>
              <Button
                variant="contained"
                href="https://cal.com/calli-fuchigami/15min"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  backgroundColor: "#FFF",
                  color: theme.palette.primary.main,
                  px: 6,
                  py: 3,
                  borderRadius: "100px",
                  fontSize: theme.typography.button.fontSize,
                  fontWeight: 700,
                  textTransform: "none",
                  boxShadow: "0 8px 16px rgba(0,0,0,0.2)",
                  "&:hover": {
                    backgroundColor: "#FFF",
                    transform: "translateY(-2px)",
                    boxShadow: "0 12px 24px rgba(0,0,0,0.3)",
                  },
                }}
              >
                Book a 15-minute call →
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Who's This For Section */}
        <Box
          sx={{
            py: { xs: 8, md: 12 },
            px: { xs: 2, md: 4 },
            backgroundColor: theme.palette.background.default,
          }}
        >
          <Grid container spacing={6} sx={{ maxWidth: 1200, mx: "auto" }}>
            <Grid item size={{ xs: 12 }}>
              <Typography
                variant="h2"
                sx={{
                  textAlign: "center",
                  mb: 6,
                  color: theme.palette.primary.main,
                }}
              >
                Who's this for?
              </Typography>
            </Grid>
            {[
              {
                icon: "👩‍⚕️",
                title: "Activity Directors",
                description:
                  "Create engaging activities without the prep work. Our games are ready to play anytime.",
              },
              {
                icon: "👵",
                title: "Residents",
                description:
                  "Enjoy familiar games with a modern twist. Simple to play, fun to share.",
              },
              {
                icon: "👨‍👩‍👧‍👦",
                title: "Visiting Families",
                description:
                  "Connect with loved ones through shared play. Perfect for visits of any length.",
              },
            ].map((persona, index) => (
              <Grid item size={{ xs: 12, md: 4 }} key={index}>
                <Box
                  sx={{
                    p: 4,
                    height: "100%",
                    backgroundColor: "#FFF",
                    borderRadius: "24px",
                    boxShadow: "0 8px 32px rgba(92, 75, 58, 0.1)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: theme.typography.h1.fontSize,
                      mb: 2,
                    }}
                  >
                    {persona.icon}
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      mb: 2,
                      color: theme.palette.text.primary,
                    }}
                  >
                    {persona.title}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: theme.typography.body1.fontSize,
                    }}
                  >
                    {persona.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
            <Grid item size={{ xs: 12 }}>
              <Typography
                variant="body1"
                sx={{
                  textAlign: "center",
                  mt: 4,
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.body1.fontSize,
                  maxWidth: 800,
                  mx: "auto",
                }}
              >
                Whether it's morning coffee, a trivia night, or a weekend family
                visit, our games bring people together — without adding work for
                your team.
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Social Proof Banner */}
        <Box
          sx={{
            py: { xs: 8, md: 12 },
            px: { xs: 2, md: 4 },
            backgroundColor: theme.palette.background.default,
            borderTop: "1px solid rgba(0,0,0,0.1)",
            borderBottom: "1px solid rgba(0,0,0,0.1)",
          }}
        >
          <Grid
            container
            spacing={4}
            sx={{ maxWidth: 1200, mx: "auto", textAlign: "center" }}
          >
            <Grid item size={{ xs: 12 }}>
              <Typography
                variant="h2"
                sx={{
                  mb: 2,
                  color: theme.palette.primary.main,
                  fontSize: theme.typography.h2.fontSize,
                }}
              >
                Trusted by leaders in engagement
              </Typography>
              <Typography
                variant="h3"
                sx={{
                  mb: 4,
                  color: theme.palette.text.secondary,
                  fontSize: theme.typography.h3.fontSize,
                  fontWeight: 400,
                }}
              >
                Flying Comet Games powers daily entertainment for communities
                like The Seattle Times and beyond.
              </Typography>
              <Box
                sx={{
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                }}
              >
                <AsSeenInSection
                  backgroundColor={theme.palette.background.default}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        <AboutUs isComponent={true} />
        <Footer />
      </Box>
    </ThemeProvider>
  );
};

export default SeniorLivingHome;
